/**
 * 🟥 CUADRO NEGRO DEFINITIVO 2026-06-28 (rev12) — Store global del Workspace.
 *
 * PROBLEMA RESUELTO:
 * En mobile, la tab "Trabajo" monta otro layout/sub-árbol que NO incluye
 * el componente Dashboard.jsx. El listener `workspace-image-published`
 * estaba registrado dentro de Dashboard.jsx → solo se ejecutaba cuando
 * Dashboard estaba montado. En mobile, al cambiar a la tab "Trabajo", el
 * usuario veía el Workspace vacío porque:
 *   1) Si Dashboard NO estaba montado → el listener nunca disparaba.
 *   2) Si Dashboard se re-montaba → el useState local volvía a null.
 *
 * SOLUCIÓN:
 * Este Provider se monta UNA VEZ a nivel App, vive durante toda la sesión
 * (no se desmonta al cambiar tabs/rutas en mobile), y:
 *   - Registra el listener `workspace-image-published` global.
 *   - Mantiene `latestWorkspaceImage` y `showImageEditor` en su propio state.
 *   - Persiste en sessionStorage para sobrevivir refrescos en mobile.
 *   - Expone `useWorkspaceImageStore()` para que CUALQUIER componente
 *     (Dashboard desktop, MobileWorkspace, etc.) lea/escriba la misma
 *     fuente de verdad.
 *
 * COMPATIBILIDAD:
 *   - Desktop / Preview: el flujo previo de Dashboard.jsx sigue funcionando
 *     porque ahora consume del store global en lugar de su useState local.
 *   - No agrega dependencias externas (Zustand/Redux). Solo React Context.
 *   - No requiere cambios en los disparadores existentes
 *     (`window.dispatchEvent(new CustomEvent('workspace-image-published', ...))`).
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const SESSION_KEY_LATEST_WS_ITEM = 'damyxia_latest_ws_item';
const SESSION_KEY_SHOW_EDITOR = 'damyxia_show_image_editor';
// 🟥 CUADRO NEGRO 2026-06-30 (Persistencia expand-workspace) — extendemos
// el store con la lista de imágenes/variantes para que sobrevivan al
// desmontaje de Dashboard.jsx cuando el usuario apreta la flechita
// "Expandir Workspace" (App.js líneas 840/858 renderizan Dashboard en
// distinta ubicación → React desmonta y monta uno nuevo, perdiendo el
// useState local). El Provider vive a nivel App y NO se desmonta.

const WorkspaceImageContext = createContext(null);

const readSessionItem = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY_LATEST_WS_ITEM);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const readSessionShow = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY_SHOW_EDITOR) === 'true';
  } catch { return false; }
};

export function WorkspaceImageProvider({ children }) {
  const [latestWorkspaceImage, _setLatestWorkspaceImage] = useState(readSessionItem);
  const [showImageEditor, _setShowImageEditor] = useState(readSessionShow);
  // 🟥 CUADRO NEGRO 2026-06-30 — lista de imágenes cargadas (originales) y
  // sus variantes. Viven en memoria del Provider (no en sessionStorage
  // porque son URLs remotas y Dashboard.jsx igualmente recarga desde
  // /api/workspace/items en cada mount). Su objetivo es evitar el flash
  // vacío al desmontar Dashboard con el botón "expand-workspace-btn".
  const [workspaceImages, setWorkspaceImages] = useState([]);
  const [workspaceVariants, setWorkspaceVariants] = useState([]);

  // Wrappers que sincronizan sessionStorage en cada cambio.
  const setLatestWorkspaceImage = useCallback((value) => {
    _setLatestWorkspaceImage(value);
    try {
      if (value) sessionStorage.setItem(SESSION_KEY_LATEST_WS_ITEM, JSON.stringify(value));
      else sessionStorage.removeItem(SESSION_KEY_LATEST_WS_ITEM);
    } catch { /* private mode / quota: state local sigue siendo la fuente válida */ }
  }, []);

  const setShowImageEditor = useCallback((value) => {
    _setShowImageEditor(!!value);
    try {
      if (value) sessionStorage.setItem(SESSION_KEY_SHOW_EDITOR, 'true');
      else sessionStorage.removeItem(SESSION_KEY_SHOW_EDITOR);
    } catch { /* ignore */ }
  }, []);

  // 🟥 Listener GLOBAL — sobrevive a cambios de tab y re-mounts.
  useEffect(() => {
    const handler = (event) => {
      try {
        if (event?.detail?.workspaceItem) {
          setLatestWorkspaceImage(event.detail.workspaceItem);
          setShowImageEditor(true);
        }
      } catch (e) {
        console.error('[WorkspaceImageStore] listener error:', e);
      }
    };
    window.addEventListener('workspace-image-published', handler);
    return () => window.removeEventListener('workspace-image-published', handler);
  }, [setLatestWorkspaceImage, setShowImageEditor]);

  const value = {
    latestWorkspaceImage,
    setLatestWorkspaceImage,
    showImageEditor,
    setShowImageEditor,
    // 🟥 CUADRO NEGRO 2026-06-30 — lista/variantes persistentes.
    workspaceImages,
    setWorkspaceImages,
    workspaceVariants,
    setWorkspaceVariants,
  };

  return (
    <WorkspaceImageContext.Provider value={value}>
      {children}
    </WorkspaceImageContext.Provider>
  );
}

/**
 * Hook que devuelve el store global del Workspace. Si por error se llama
 * fuera del Provider, devuelve un fallback inocuo (no rompe el árbol).
 */
export function useWorkspaceImageStore() {
  const ctx = useContext(WorkspaceImageContext);
  if (!ctx) {
    // Defensa: en caso de que un componente lo use fuera del Provider
    // (no debería ocurrir tras este wiring), devolver un stub no-op.
    return {
      latestWorkspaceImage: null,
      setLatestWorkspaceImage: () => {},
      showImageEditor: false,
      setShowImageEditor: () => {},
      workspaceImages: [],
      setWorkspaceImages: () => {},
      workspaceVariants: [],
      setWorkspaceVariants: () => {},
    };
  }
  return ctx;
}

export default WorkspaceImageContext;
